import type { PlayerArchetypeEligibility } from '@/api/generated/models'

// Auth
export interface LoginRequest {
  usernameOrEmail: string
  password: string
}

export interface LoginResponse {
  userId: number
  username: string
  email: string
  userStatus: string
  accessToken: string
  tokenExpiration: string
}

// User
export interface UserSettings {
  blurScores: boolean
  hideUserRecords: boolean
  timeZone: string
  newDashboard: boolean
  hideSandbox: boolean
  // Feature-flags v2 settings-unlock demo (throwaway; removed when the demo
  // retires). Only meaningful/rendered when UserApiResponse.sampleFeatureUnlocked
  // is true for this user.
  sampleUserSetting?: boolean
}

// Flat API response shape. GET /User/CurrentUser returns { user, leagues, settings };
// GET /User/{id}/Settings returns { user, settings }. (Bare GET /User/{id} returns the
// flat User directly and is read without this normalizer.)
export interface UserApiResponse {
  user: UserData
  settings?: UserSettings
  leagues?: UserLeagueEntry[]
  // Resolved feature-flag map for the current user (flag key -> on/off).
  // Null/absent when the server hasn't resolved any flags for the session.
  features?: Record<string, boolean> | null
  // Feature-flags v2 settings-unlock demo — whether FeatureFlags.SampleUserFeature
  // is unlocked for this user. Sibling to `settings`, not part of it (mirrors
  // LeagueSettingsResponse.sampleFeatureUnlocked on the league side). Gates
  // whether the "Feature Flag Demo" section renders at all.
  sampleFeatureUnlocked?: boolean
}

export interface UserData {
  id: number
  userName: string  // Note: capital N in API response
  email: string
  hasVerifiedEmail: boolean
  pendingEmail?: string | null
  pendingVerifyEmail: boolean
  dateRegistered: string
  lastLogon: string | null
  isNewUser: boolean
  franchiseTeamCount: number
  rookieTeamCount: number
  canCreateTeam: boolean
  canRecruitTeam: boolean
  hasSandbox: boolean
  sandboxLeagueId: number | null
  hasTestAccess: boolean
  isAdmin: boolean
  isSuperUser: boolean
}

// Normalized user shape used throughout the app
export interface User {
  id: number
  username: string
  email: string
  hasVerifiedEmail: boolean
  pendingEmail: string | null
  pendingVerifyEmail: boolean
  dateRegistered: string
  lastLogon: string | null
  isNewUser: boolean
  franchiseTeamCount: number
  rookieTeamCount: number
  canCreateTeam: boolean
  canRecruitTeam: boolean
  hasSandbox: boolean
  sandboxLeagueId: number | null
  hasTestAccess: boolean
  isAdmin: boolean
  isSuperUser: boolean
  settings?: UserSettings
  leagues: UserLeague[]
  // Resolved feature flags for this user. Populated by normalizeUserResponse
  // (raw ?? {}); may be undefined on hand-constructed User objects — consumers
  // use `useFeature`, which falls back to false.
  features?: Record<string, boolean>
  // See UserApiResponse.sampleFeatureUnlocked.
  sampleFeatureUnlocked?: boolean
}

// Normalize the raw API response into the app's User shape
export function normalizeUserResponse(raw: UserApiResponse): User {
  const u = raw.user
  const leagues = (raw.leagues ?? []).map(entry => ({
    id: entry.league.id,
    name: entry.league.name,
    abbreviation: entry.league.abbreviation,
    leagueType: entry.league.leagueType,
    isCommissioner: entry.isCommish,
    teams: entry.teams.map(t => ({
      id: t.id,
      name: t.name,
      city: t.city,
      fullName: t.fullName,
      abbreviation: t.abbreviation,
    })),
  }))

  return {
    id: u.id,
    username: u.userName,
    email: u.email,
    hasVerifiedEmail: u.hasVerifiedEmail,
    pendingEmail: u.pendingEmail ?? null,
    pendingVerifyEmail: u.pendingVerifyEmail,
    dateRegistered: u.dateRegistered,
    lastLogon: u.lastLogon,
    isNewUser: u.isNewUser,
    franchiseTeamCount: u.franchiseTeamCount,
    rookieTeamCount: u.rookieTeamCount,
    canCreateTeam: u.canCreateTeam,
    canRecruitTeam: u.canRecruitTeam,
    hasSandbox: u.hasSandbox,
    sandboxLeagueId: u.sandboxLeagueId ?? null,
    hasTestAccess: u.hasTestAccess,
    isAdmin: u.isAdmin,
    isSuperUser: u.isSuperUser,
    settings: raw.settings,
    leagues,
    features: raw.features ?? {},
    sampleFeatureUnlocked: raw.sampleFeatureUnlocked,
  }
}

export interface UserLeagueEntry {
  league: {
    id: number
    name: string
    abbreviation: string
    leagueType: string
  }
  isCommish: boolean
  teams: UserLeagueTeam[]
}

export interface UserLeague {
  id: number
  name: string
  abbreviation: string
  leagueType: string
  teams: UserLeagueTeam[]
  isCommissioner: boolean
}

export interface UserLeagueTeam {
  id: number
  name: string
  city: string
  fullName: string
  abbreviation: string
}

// Shared
export interface ErrorResult {
  message: string
}

export interface ChangePasswordRequest {
  oldPassword: string
  newPassword: string
}

// ===== Dashboard API Response =====

// GET /User/{id}/Dashboard response
export interface DashboardApiResponse {
  userId: number
  userName: string
  email?: string
  lastLogon?: string
  leagues: DashboardLeagueEntry[]
}

// Season phase, from currentSeason.status on the dashboard API (the backend
// SeasonStatus enum).
export type SeasonPhaseStatus =
  | 'preseason'
  | 'regularSeason'
  | 'divisionPlayoffs'
  | 'conferencePlayoffs'
  | 'worldSeries'
  | 'completed'

export interface DashboardLeagueEntry {
  league: {
    id: number
    name: string
    abbreviation: string
    leagueType: string  // 'franchise' | 'sandbox' | etc
  }
  currentSeason?: {
    id: number
    year: number
    day: number
    status?: SeasonPhaseStatus
  }
  seasonTimeline?: {
    currentDate: string
    milestones: SeasonMilestone[]
  }
  isCommish: boolean
  lowMinorsLimit: number
  simRate: number | null
  isPaused: boolean
  isAdminHold: boolean
  forumId: number | null
  hasAwards: boolean
  hasDraft: boolean
  hasFreeAgents: boolean
  hasTrades: boolean
  hasTransactions: boolean
  hasSettings: boolean
  teams: DashboardTeamEntry[]
  pendingTeamRequests?: TeamRequest[]
}

export interface SeasonMilestone {
  type: string  // 'preseasonStart' | 'regularSeasonStart' | 'playoffRoundStart' | 'seasonEnd' | etc
  description: string
  date: string  // ISO datetime string
}

export interface DashboardTeamEntry {
  team: {
    id: number
    city: string
    name: string
    abbreviation: string | null
    fullName: string
  }
  // Wire property is `standings` (UserDashboardTeam.standings). Was previously
  // read as `teamStandings`, which never matched — so the dashboard's W-L/place
  // line was always blank and the "Hide standings & records" toggle looked dead.
  standings?: {
    year: number
    wins: number
    losses: number
    gamesBehind: number
    winPct: number
    place: number
  }
  upcomingDraftPicks?: DraftPickRaw[]
  pendingTradeOfferCount: number
  lastGame?: GameDashboardData | null
  nextGame?: GameDashboardData | null
  lowMinorsCount: number
  // Official team color (entry-level on TeamDashboardData, #429).
  primaryColor?: string | null
  secondaryColor?: string | null
}

// Raw draft pick from API
export interface DraftPickRaw {
  teamId: number
  season: {
    year: number
    currentDate: string
    id: number
  }
  round: number
  pickNumber: number
  overallPickNumber: number
  pickDay: number
  id: number
}

// Normalized draft pick for the app
export interface DraftPick {
  round: number
  overallPickNumber: number
  pickDay: number
  seasonYear: number
}

export interface GameDashboardData {
  gameId: number
  day: number
  team: { id: number; city: string; name: string; fullName: string }
  opponent: { id: number; city: string; name: string; fullName: string }
  homeAway: 'home' | 'away'
  startingPitcher: { id: number; firstName: string; lastName: string; fullName: string; throws?: number }
  opponentStartingPitcher: { id: number; firstName: string; lastName: string; fullName: string; throws?: number }
}

export interface TeamRequest {
  id: number
  userId: number
  userName: string
  comments: string | null
  dateRequested: string
}

// Normalized dashboard data for the app
export interface DashboardData {
  userId: number
  username: string
  email?: string
  lastLogon?: string
  leagues: DashboardLeague[]
}

export interface DashboardLeague {
  id: number
  name: string
  abbreviation: string
  leagueType: string
  isCommissioner: boolean
  isPaused: boolean
  isAdminHold: boolean
  forumId: number | null
  lowMinorsLimit: number
  simRate: number | null
  hasAwards: boolean
  hasDraft: boolean
  hasFreeAgents: boolean
  hasTrades: boolean
  hasTransactions: boolean
  hasSettings: boolean
  currentSeason?: { id: number; year: number; day: number; status?: SeasonPhaseStatus }
  seasonTimeline?: { currentDate: string; milestones: SeasonMilestone[] }
  pendingTeamRequests: TeamRequest[]
  teams: DashboardTeam[]
}

export interface DashboardTeam {
  id: number
  city: string
  name: string
  fullName: string
  abbreviation: string | null
  /** Official team-level color (teams.primarycolor, #286/#429). Drives the
   *  dashboard league-card header — one team per league, so the league name
   *  rides the owner's team color. Null/absent when the owner hasn't set one. */
  primaryColor?: string | null
  secondaryColor?: string | null
  standings?: {
    wins: number
    losses: number
    gamesBehind: number
    winPct: number
    place: number
  }
  upcomingDraftPicks: DraftPick[]
  pendingTradeOfferCount: number
  lastGame: GameDashboardData | null
  nextGame: GameDashboardData | null
  lowMinorsCount: number
}

// Normalize the raw dashboard API response into the app's DashboardData shape
export function normalizeDashboardResponse(raw: DashboardApiResponse): DashboardData {
  const leagues = (raw.leagues ?? []).map(entry => ({
    id: entry.league.id,
    name: entry.league.name,
    abbreviation: entry.league.abbreviation,
    leagueType: entry.league.leagueType,
    isCommissioner: entry.isCommish,
    isPaused: entry.isPaused,
    isAdminHold: entry.isAdminHold,
    forumId: entry.forumId,
    lowMinorsLimit: entry.lowMinorsLimit,
    simRate: entry.simRate,
    hasAwards: entry.hasAwards,
    hasDraft: entry.hasDraft,
    hasFreeAgents: entry.hasFreeAgents,
    hasTrades: entry.hasTrades,
    hasTransactions: entry.hasTransactions,
    hasSettings: entry.hasSettings,
    currentSeason: entry.currentSeason,
    seasonTimeline: entry.seasonTimeline,
    pendingTeamRequests: entry.pendingTeamRequests ?? [],
    teams: entry.teams.map(t => ({
      id: t.team.id,
      city: t.team.city,
      name: t.team.name,
      fullName: t.team.fullName,
      abbreviation: t.team.abbreviation,
      standings: t.standings ? {
        wins: t.standings.wins,
        losses: t.standings.losses,
        gamesBehind: t.standings.gamesBehind,
        winPct: t.standings.winPct,
        place: t.standings.place,
      } : undefined,
      upcomingDraftPicks: (t.upcomingDraftPicks ?? []).map(dp => ({
        round: dp.round,
        overallPickNumber: dp.overallPickNumber,
        pickDay: dp.pickDay,
        seasonYear: dp.season.year,
      })),
      pendingTradeOfferCount: t.pendingTradeOfferCount,
      lastGame: t.lastGame ?? null,
      nextGame: t.nextGame ?? null,
      lowMinorsCount: t.lowMinorsCount,
      primaryColor: t.primaryColor ?? null,
      secondaryColor: t.secondaryColor ?? null,
    })),
  }))

  return {
    userId: raw.userId,
    username: raw.userName,
    email: raw.email,
    lastLogon: raw.lastLogon,
    leagues,
  }
}

// ===== Config API =====

export interface UserConfiguration {
  userId: number
  entityTypeId: number
  entityId: number | null
  configurationValue: string  // JSON string
}

export interface SaveConfigurationRequest {
  entityType: string | number  // 'User' | 'League' | 'Team' | 'Contest' or int
  entityId?: number | null
  configurationValue: string  // JSON string
  userId?: number
}

export interface DeleteConfigurationRequest {
  entityType: string | number
  entityId?: number | null
  userId?: number
}

// ===== Records API =====

export interface UserRecord {
  teamId: number | null
  city: string | null
  name: string | null
  abbr: string | null
  leagueId: number | null
  league: string | null
  firstYear: number | null
  lastYear: number | null
  seasons: number
  wins: number
  losses: number
  runsScored: number
  runsAllowed: number
  avgPlace: number
  winPct60Plus: number
  winPct55to59: number
  winPct50to54: number
  winPct45to49: number
  winPct40to44: number
  winPctUnder40: number
  playoffAppearances: number
  playoffWins: number
  playoffLosses: number
  wsAppearances: number
  wsWins: number
  place1: number
  maxWinPct: number
}

export interface UserTimelineEntry {
  simYear: number | null
  seasons: number
  wins: number
  losses: number
  avgPlace: number
  playoffAppearances: number
  playoffWins: number
  playoffLosses: number
  wsWins: number
}

export interface UserTeamRequest {
  teamRequestId: number
  teamId: number
  teamName: string
  leagueId: number
  league: string
  userComments: string | null
  dateRequested: string
  status: string  // 'pending' | 'accepted' | 'declined'
  responseUsername: string | null
  responseComments: string | null
}

export interface WorldSeriesAppearance {
  leagueId: number
  league: string | null
  seasonId: number
  year: number
  teamId: number
  city: string | null
  name: string | null
  wins: number
  losses: number
  place: number
  opponentTeamId: number
  opponentCity: string | null
  opponentName: string | null
  wsWins: number
  wsLosses: number
}

// ===== Team Create =====

export interface TeamCreateTemplate {
  teamCreateTemplateId: number
  name: string
  description: string
  strength1: string
  strength2: string
  discipline: number
  contact: number
  batting: number
  slugging: number
  speed: number
  power: number
  finesse: number
  control: number
  specialty: number
  holdRunner: number
  infieldFielding: number
  outfieldFielding: number
}

export interface RegisterAndCreateTeamRequest {
  teamCreateTemplateId: number
  teamName: string
  teamCity: string
  username: string
  password: string
  email: string
  referralCode?: string
  recaptchaResponse: string
}

// ===== League Component API Response =====

// Raw shape of GET /League/{id} (with optional components)
// League.freeAgencyBidsPermitted is a computed bool on the C# record
// League.freeAgentBidVisibilityType serializes as camelCase enum string
// (e.g. "blindBidding" | "teamVisibleOnly" | "teamAndBidVisible")
export interface LeagueApiData {
  id: number
  name: string
  abbreviation: string | null
  leagueType: string | null
  currentYear: number
  currentDate?: string
  // Sim-clock status (#854) — drives the amber Paused / red Admin Hold badge in
  // the page header. Optional so bundles built against a pre-#854 API don't break.
  isPaused?: boolean
  isAdminHold?: boolean
  currentSeasonDay: number
  currentSeasonDayRegularSeasonEnds: number | null
  currentSeasonDayPlayoffsStart: number | null
  // Draft scheduling for the current season. The API has always sent these; this
  // hand-written adapter simply dropped them, which is why the draft page showed
  // "Pending" for every upcoming pick. Null when there is no current season
  // (LeagueSelect LEFT-JOINs the season row); simRate is coalesced, so non-null.
  currentSeasonDayDraftStarts?: number | null
  currentSeasonDayDraftEnds?: number | null
  currentSeasonSimRate?: number
  // Large-league guard: server hides the playoffs page (endpoint 404s) when the
  // bracket would be too large. Don't show a pill that leads to a 404.
  hidePlayoffsLink: boolean
  leagueCap: number
  hardCap: boolean
  bindingContracts: boolean
  hasAwards: boolean
  hasDraft: boolean
  hasRegularSeason: boolean
  isExhibition: boolean
  hasFreeAgents: boolean
  hasTrades: boolean
  hasAging: boolean
  hasHistory: boolean
  hasTransactions: boolean
  freeAgencyBidsPermitted: boolean
  freeAgentBidVisibilityType: string  // 'blindBidding' | 'teamVisibleOnly' | 'teamAndBidVisible' | 'none'
  currentSeasonFreeAgentBidVisibilityType: string | null
  currentSeasonUFADays: number | null
  freeAgencyTimingId: number
  freeAgencyTimingName: string
  currentLeagueState: string
  // Current-season overrides (may be null if no active season override)
  currentSeasonHardCap?: boolean | null
  currentSeasonBindingContracts?: boolean | null
  currentSeasonFreeAgencyTimingName?: string | null
}

// ===== Free Agency Types =====

// Shape of each PlayerRating as serialized by the API
// current/potential are strings (numeric or letter-grade depending on league settings).
// No tier/level field: the API deliberately doesn't ship one — the tier's only
// consumer is a Tailwind class map, so the client derives it from the value
// (`ratingColorForValue`).
export interface PlayerRatingData {
  current: string
  potential: string | null
}

// PlayerEndurance extends PlayerRating with remaining/seasonFatigue
export interface PlayerEnduranceData extends PlayerRatingData {
  remaining: string | null
  seasonFatigue: number | null
}

// Raw API shape for a Player (returned by GET /League/{id}/freeagents)
// Property names are camelCase (API uses JsonNamingPolicy.CamelCase)
// Position serializes via EnumMember values: "P", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "DH"
export interface FreeAgentApiResponse {
  id: number
  firstName: string
  lastName: string
  fullName: string
  position: string   // enum member value e.g. "P", "1B", "SS"
  positionOrdinal: number
  // Multi-position card. The endpoint returns the shared `Player`, which always
  // DECLARED this but did not serve it until GH #1068 -- with WhenWritingNull an
  // unpopulated property is absent from the JSON entirely, so nothing errored and
  // the generated model still said it existed. Absent in a Standard league, for a
  // player with no archetype, and for every pitcher.
  eligibility?: PlayerArchetypeEligibility
  rosterLevel: string | null
  rosterLevelOrdinal: number | null
  bio: {
    age: number | null
    experience: number | null
    firstYear: number | null
    lastYear: number | null
    bats: string
    throws: string
    heightInches: number | null
    height: string | null
    weight: number | null
    chanceToRetire: number | null
  }
  ratings: {
    overall: PlayerRatingData
    overallHitting: PlayerRatingData
    overallPitching: PlayerRatingData
    overallFielding: PlayerRatingData
    di: PlayerRatingData   // discipline
    cn: PlayerRatingData   // contact
    ba: PlayerRatingData   // batting
    sl: PlayerRatingData   // slugging
    sp: PlayerRatingData   // speed
    po: PlayerRatingData   // power
    fi: PlayerRatingData   // finesse
    co: PlayerRatingData   // control
    sy: PlayerRatingData   // specialty
    hr: PlayerRatingData   // hold runner
    ra: PlayerRatingData   // range
    gl: PlayerRatingData   // glove
    ar: PlayerRatingData   // arm
    en: PlayerEnduranceData
  }
  // quickStats is populated from Stats.Create(x, Timeframe.CurrentSeason) in LeagueRepository.
  // Stats.G maps to sg (season games played). Null when the player has never played.
  // Legacy filter uses quickStats.g === 'undefined' to detect LM-eligible rookies.
  quickStats: {
    g: number | null
  } | null
  // Career stat totals (#133) — projected onto the free-agents player by the
  // API (LeaguePlayerCareerStats). Drives the Stats-view columns. Absent on
  // older bundles; nullable fields throughout.
  careerStats?: FreeAgentCareerStats | null
  salary: {
    base: number | null
    over: number | null
    total: number
  } | null
  canSignNow: boolean | null
  signDay: number | null
  notationShort: string | null
  notationLong: string | null
  pendingDrop: boolean | null
  pendingTradeId: number | null
  pendingTransaction: boolean | null
  team: {
    id: number
    city: string
    name: string
    abbreviation: string | null
    fullName: string | null
  } | null
  league: {
    id: number
    name: string
    abbreviation: string | null
    leagueType: string | null
  } | null
}

// Raw API shape for GET /League/{id}/freeagentbids
// offerId, bidAmount are int; dropPlayers is IEnumerable<int> (may be null)
export interface FreeAgentBidApiResponse {
  offerId: number
  player: {
    id: number
    firstName: string
    lastName: string
    fullName: string
  } | null
  team: {
    id: number
    city: string
    name: string
    abbreviation: string | null
    fullName: string | null
  } | null
  user: {
    id: number
    userName: string
  } | null
  dateOffered: string | null  // ISO datetime string
  bidAmount: number | null
  dropPlayers: number[] | null
}

// Request body for PUT /Team/League/{id}/FreeAgentBid
// Maps to PlaceFreeAgentBidDto on the server
export interface PlaceFreeAgentBidRequest {
  playerId: number
  bidAmount: number
  dropPlayerIds: number[]
}

// ===== Team financials (free-agency offer drawer) =====

// Normalized shape computed from GET /Team/League/{id}/Roster + LeagueApiData for the offer drawer.
// capSpace = leagueCap - currentSalary (computed by caller from league and roster data)
// rosterPlayers uses the full FreeAgent shape so the drawer can render a full FA-style table.
export interface TeamFinancials {
  teamId: number
  capSpace: number
  currentSalary: number
  rosterPlayers: FreeAgent[]
}

// ===== Free Agency Flat View-Models =====

// Flat view-model used by FreeAgencyPage and OfferDrawer.
// Produced by normalizeFreeAgent() in client.ts from FreeAgentApiResponse.
export interface FreeAgent {
  id: number
  firstName: string
  lastName: string
  fullName: string
  position: string
  positionOrdinal: number
  eligibility?: PlayerArchetypeEligibility
  age: number | null
  experience: number | null
  // games played (season) — from quickStats.g on the wire.
  // Null means the player has never appeared in a game (LM eligible).
  games: number | null
  bats: string
  throws: string
  // Roster level for rostered players (Active / AAA / Low Minors).
  // Null for actual free agents — the API only populates this on
  // players already on a roster (used by the OfferDrawer's
  // sign-and-drop list to surface where each candidate sits).
  rosterLevel?: string | null
  // Rating fields are kept as strings — they may be numeric ("75") or letter grades ("B")
  // depending on league settings. UI parses to number on demand.
  overall: string        // OVR
  overallHitting: string // OVRb
  overallPitching: string // OVRp
  overallFielding: string // OVRf
  di: string  // discipline
  cn: string  // contact
  ba: string  // batting
  sl: string  // slugging
  sp: string  // speed
  po: string  // power
  fi: string  // finesse
  co: string  // control
  sy: string  // specialty
  hr: string  // hold runner
  ra: string  // range
  gl: string  // glove
  ar: string  // arm
  en: string  // endurance
  // Potential rating fields — parallel to each current rating.
  // Empty string when no potential data is available.
  potentialOverall: string
  potentialOverallHitting: string
  potentialOverallPitching: string
  potentialOverallFielding: string
  potentialDi: string
  potentialCn: string
  potentialBa: string
  potentialSl: string
  potentialSp: string
  potentialPo: string
  potentialFi: string
  potentialCo: string
  potentialSy: string
  potentialHr: string
  potentialRa: string
  potentialGl: string
  potentialAr: string
  potentialEn: string
  // Legacy aliases for backward compat (still used in OfferDrawer display)
  contact: string   // == cn
  power: string     // == po
  speed: string     // == sp
  defense: string   // == gl
  salary: number    // == api.salary?.total ?? 0
  canSignNow: boolean
  isUfa: boolean    // == canSignNow (computed: free agent who can be signed immediately)
  notationShort: string | null
  // Long-form notation (e.g., "Preseason Free Agent"). Rendered as a
  // title-attribute tooltip on the short-form badge next to the name.
  notationLong: string | null
  // Days offset from April 1 of the league's current season year when
  // this player will sign at their listed demand. Null when no signing
  // date applies (e.g., player is still in season or non-applicable).
  // Used to render the "Will sign: Apr 14" line.
  signDay: number | null
  // Career stat totals (#133) — drives the Stats-view column set on the
  // Pitchers / Batters tabs (mirrors league/players). Null/absent when the
  // player has no career line.
  career?: FreeAgentCareerStats | null
}

// Subset of the API's LeaguePlayerCareerStats carrying just the totals the
// FA Stats view renders. Mirrors league/players' helpers `CareerStats`. All
// nullable — a genuine null/undefined renders "—" (no career row), distinct
// from a real 0.
export interface FreeAgentCareerStats {
  sg?: number | null
  // batting
  sab?: number | null
  sr?: number | null
  shr?: number | null
  srbi?: number | null
  ssb?: number | null
  savg?: number | null
  sobp?: number | null
  sslg?: number | null
  sops?: number | null
  // pitching
  sgs?: number | null
  sw?: number | null
  sl?: number | null
  ssv?: number | null
  sip?: number | null
  sso?: number | null
  sera?: number | null
  swhip?: number | null
}

// Flat view-model bid used by FreeAgencyPage and OfferDrawer.
// teamFullName is plumbed through for the OfferDrawer's "Other bids"
// section (team-visible + open-bidding leagues display team names);
// blind-bidding leagues won't render the name even though it's
// populated.
export interface FreeAgentBid {
  offerId: number
  playerId: number
  teamId: number
  teamFullName: string
  userId: number
  bidAmount: number
  dropPlayerIds: number[]
}
